import { useEffect, useState } from "react";

import useAxios from "@hooks/use-axios";
import { CONTRACT_POLL_MS } from "../misc/constants";
import { normalizeContractCounts, normalizePublisherContract } from "../misc/helpers";

const DEFAULT_LIMIT = 20;

// The services wrap their payload in `data` at one or two levels depending on
// the gateway in front of them, so unwrap defensively rather than indexing.
const payloadOf = (res) => {
    const body = res?.data ?? res;
    return body?.data ?? body;
};

/**
 * One brand's publisher contracts, paged on the server. Ops and the brand
 * portal get the same response from `/contract/publishers`; only the service
 * and the path differ, so both screens are fed by this one hook.
 *
 * Sending and Sign all only queue the work, so while any row is still queued
 * the list re-reads itself on a timer until the job has caught up.
 * @param base the useAxios base — "ops" or "brand"
 * @param path the list endpoint, or "" while the account id is unknown
 */
const usePublisherContracts = ({ base, path }) => {
    const { axiosInstance, loading } = useAxios({ base, initialLoader: true, showAlert: false });
    // Polls through its own instance, so the table does not flash its loader
    // every few seconds.
    const { axiosInstance: pollInstance } = useAxios({ base, showAlert: false });

    const [publishers, setPublishers] = useState([]);
    const [counts, setCounts] = useState(normalizeContractCounts());
    const [pagination, setPagination] = useState({ total: 0, page: 1, limit: DEFAULT_LIMIT });

    const fetchPublishers = async (page = 1, limit = DEFAULT_LIMIT, instance = axiosInstance) => {
        if (!path) return;

        const data = await instance
            .get(path, { params: { page, limit } })
            .then(payloadOf)
            .catch(() => null) || {};
        const items = data.items || [];

        setPublishers(items.map(normalizePublisherContract));
        setCounts(normalizeContractCounts(data.counts));
        setPagination({
            total: data.total ?? items.length,
            page : data.page ?? page,
            limit: data.limit ?? limit
        });
    };

    useEffect(() => {
        fetchPublishers(1, DEFAULT_LIMIT);
    }, [path]); // eslint-disable-line react-hooks/exhaustive-deps

    const pending = counts.queued + counts.brandSignQueued > 0;

    useEffect(() => {
        if (!pending) return undefined;

        const timer = window.setInterval(
            () => fetchPublishers(pagination.page, pagination.limit, pollInstance),
            CONTRACT_POLL_MS
        );

        return () => window.clearInterval(timer);
    }, [pending, path, pagination.page, pagination.limit]); // eslint-disable-line react-hooks/exhaustive-deps

    return {
        publishers,
        counts,
        pagination,
        loading,
        handlePageChange   : (limit, page) => fetchPublishers(page, limit),
        handlePerRowsChange: (limit) => fetchPublishers(1, limit),
        refetch            : () => fetchPublishers(pagination.page, pagination.limit)
    };
};

export default usePublisherContracts;
